import type { FC, PropsWithChildren } from 'react'
import { forwardRef } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import Elevation from '@segment/matcha/components/Elevation'

import Connector from '@segment/matcha/recipes/Connector'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import EventImage from './EventImage'
import ScreenImage from './ScreenImage'
import CardProfileImage from './CardProfileImage'

export const Persona = forwardRef<HTMLDivElement>(({ ...rest }, ref) => (
  <SectionComponent variant="noCopy">
    <Wrapper ref={ref} {...rest}>
      <Canvas>
        <ConnectorArea>
          <ConnectorLine />
          <ConnectorCurve />
        </ConnectorArea>
        <ScreenArea>
          <ElevationComponent elevation={4}>
            <ScreenImage />
          </ElevationComponent>
        </ScreenArea>
        <EventArea>
          <EventElevationComponent elevation={2}>
            <EventImage />
          </EventElevationComponent>
        </EventArea>
        <CardArea>
          <ElevationComponent elevation={4}>
            <CardProfileImage />
          </ElevationComponent>
        </CardArea>
      </Canvas>
    </Wrapper>
  </SectionComponent>
))

const ConnectorLine: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()
  return (
    <ConnectorLineComponent
      id="personas_line_connector"
      path="M192 8L0 8"
      width={192}
      height={16}
      gradient={
        <>
          <stop stopColor={theme.palette.twilio.green[50]} stopOpacity="0" />
          <stop offset="0.515271" stopColor={theme.palette.twilio.green[50]} />
          <stop offset="1" stopColor={theme.palette.twilio.green[50]} stopOpacity="0" />
        </>
      }
      gradientDots={
        <>
          <stop stopColor={theme.palette.neutrals.white[100]} stopOpacity="0" />
          <stop offset="0.489183" stopColor={theme.palette.neutrals.white[100]} />
          <stop offset="1" stopColor={theme.palette.neutrals.white[100]} stopOpacity="0" />
        </>
      }
    />
  )
}

const ConnectorCurve: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()

  return (
    <ConnectorCurveComponent
      id="personas_curve_connector"
      path="M2.68262 8L49.0826 8C71.1074 8 82.1198 8 90.5618 12.2182C98.3103 16.0899 104.593 22.3723 108.464 30.1209C112.683 38.5628 112.683 49.5752 112.683 71.6L112.683 96.5001"
      width={121}
      height={97}
      gradient={
        <>
          <stop stopColor={theme.palette.twilio.green[50]} stopOpacity="0" />
          <stop offset="0.515271" stopColor={theme.palette.twilio.green[50]} />
          <stop offset="1" stopColor={theme.palette.twilio.green[50]} stopOpacity="0" />
        </>
      }
      gradientDots={
        <>
          <stop stopColor={theme.palette.neutrals.white[100]} stopOpacity="0" />
          <stop offset="0.489183" stopColor={theme.palette.neutrals.white[100]} />
          <stop offset="1" stopColor={theme.palette.neutrals.white[100]} stopOpacity="0" />
        </>
      }
      gradientRotation={75}
    />
  )
}

const SectionComponent = styled(SectionBase)`
  overflow-x: visible;
`

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Canvas = styled(Section.Contents)`
  display: grid;
  grid-template-columns: repeat(12, auto);
  grid-template-rows: repeat(9, auto);
  max-width: 1100px;
`

const ScreenArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  grid-column-start: 1;
  grid-column-end: 6;
  grid-row-start: 1;
  grid-row-end: 6;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-column-start: 1;
    grid-column-end: 4;
    grid-row-start: 2;
    grid-row-end: 8;
  }
`

const EventArea = styled.div`
  display: grid;
  z-index: 1;
  grid-column-start: 1;
  grid-column-end: 6;
  grid-row-start: 2;
  grid-row-end: 3;
  margin-left: -1vw;
  margin-right: -1vw;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-column-start: 1;
    grid-column-end: 4;
    grid-row-start: 4;
    grid-row-end: 5;
  }
`

const CardArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  grid-column-start: 4;
  grid-column-end: 13;
  grid-row-start: 4;
  grid-row-end: 9;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-column-start: 6;
    grid-column-end: 13;
    grid-row-start: 1;
    grid-row-end: 9;
  }
`

const ConnectorArea = styled.div`
  display: flex;
  justify-items: start;
  align-items: end;
  grid-column-start: 6;
  grid-column-end: 11;
  grid-row-start: 2;
  grid-row-end: 4;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding-top: 3vw;
  }

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-top: 0;
    justify-items: center;
    align-items: center;
    grid-column-start: 4;
    grid-column-end: 6;
    grid-row-start: 4;
    grid-row-end: 5;
  }
`

const ConnectorLineComponent = styled(Connector)`
  width: 100%;
  display: none;
  opacity: 0.5;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: block;
  }
`

const ConnectorCurveComponent = styled(Connector)`
  width: 100%;
  display: block;
  opacity: 0.5;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: none;
  }
`

const ElevationComponent = styled(Elevation)`
  border-radius: 10px;
  border: 2px solid ${({ theme }) => theme.palette.twilio.gray[20]};
`

const EventElevationComponent = styled(Elevation)`
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.palette.twilio.gray[20]};
`
